import React, { useContext, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "../../../design-system";
import { userContext } from "../../../../stores/users/userContext";
import { hostApi } from "../../../constants";
import apiRequest from "../../../../utils/apiClient";

interface SearchResult {
  sentence_id: number;
  sentence: string;
  start: number;
  end: number;
  transcript_id: number;
  transcript_title: string;
}

function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#e53935", color: "#fff", borderRadius: "2px", padding: "0 2px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function formatTime(seconds: number): string {
  if (!seconds && seconds !== 0) return "";
  const s = Math.round(seconds / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

const SearchTab: React.FC = () => {
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const runSearch = async (q: string) => {
    if (q.trim().length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await apiRequest(`${hostApi}cross-video-search/?q=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") runSearch(query);
  };

  const openInET = (result: SearchResult) => {
    userStore.setUserTranscript(null, result.transcript_title, result.transcript_id, 0);
    navigate("/create-video/");
  };

  // Group results by transcript
  const grouped: Record<number, { title: string; results: SearchResult[] }> = {};
  for (const r of results) {
    if (!grouped[r.transcript_id]) {
      grouped[r.transcript_id] = { title: r.transcript_title, results: [] };
    }
    grouped[r.transcript_id].results.push(r);
  }
  const groups = Object.entries(grouped);

  return (
    <Box sx={{ backgroundColor: "#1c1c1e", borderRadius: "16px", padding: "24px", minHeight: "400px" }}>
      {/* Search bar */}
      <Box sx={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Search across all your uploads — e.g. "inflation" or "I love you"'
          style={{
            flex: 1,
            background: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: "8px",
            color: "#fff",
            fontFamily: "Outfit, sans-serif",
            fontSize: "15px",
            padding: "10px 16px",
            outline: "none",
          }}
          autoFocus
        />
        <button
          onClick={() => runSearch(query)}
          disabled={loading || query.trim().length < 2}
          style={{
            background: query.trim().length >= 2 ? "#e53935" : "#444",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 24px",
            fontFamily: "Outfit, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            cursor: query.trim().length >= 2 ? "pointer" : "not-allowed",
            transition: "background 0.2s",
            flexShrink: 0,
          }}
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </Box>

      {/* Results */}
      {!searched && (
        <Typography sx={{ color: "#555", fontFamily: "Outfit, sans-serif", fontSize: "14px", textAlign: "center", marginTop: "60px" }}>
          Type a word or phrase and press Enter to search across all your uploaded videos
        </Typography>
      )}

      {searched && !loading && results.length === 0 && (
        <Typography sx={{ color: "#666", fontFamily: "Outfit, sans-serif", fontSize: "14px", textAlign: "center", marginTop: "60px" }}>
          No results found for "{query}"
        </Typography>
      )}

      {groups.map(([transcriptId, group]) => (
        <Box key={transcriptId} sx={{ marginBottom: "24px" }}>
          {/* Transcript header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 700, color: "#e53935" }}>
              {group.title}
            </Typography>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#555" }}>
              {group.results.length} match{group.results.length !== 1 ? "es" : ""}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <button
              onClick={() => openInET(group.results[0])}
              style={{
                background: "transparent",
                border: "1px solid #444",
                borderRadius: "6px",
                color: "#aaa",
                fontFamily: "Outfit, sans-serif",
                fontSize: "11px",
                padding: "4px 10px",
                cursor: "pointer",
              }}
            >
              Open video →
            </button>
          </Box>

          {/* Sentences */}
          {group.results.map((r) => (
            <Box
              key={r.sentence_id}
              onClick={() => openInET(r)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                padding: "10px 14px",
                marginBottom: "4px",
                background: "#242424",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { background: "#2e2e2e" },
                transition: "background 0.15s",
              }}
            >
              <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "11px", color: "#555", flexShrink: 0, marginTop: "2px", minWidth: "36px" }}>
                {formatTime(r.start)}
              </Typography>
              <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", color: "#ccc", lineHeight: 1.5, flex: 1 }}>
                {highlight(r.sentence, query)}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default observer(SearchTab);
